/**
 * Add Device Type Form Schema
 * 
 * Defines the validation for device type creation.
 * deviceTypeNumber is auto-generated by the backend.
 * 
 * Form metadata (name, title, description) is loaded from form.yml
 */

import { z } from 'zod'
import { FormConfigLoader } from '@/lib/formConfigLoader'
import formYaml from './form.yml?raw'

// Load form configuration from YAML
const formConfig = FormConfigLoader.loadFormConfig(formYaml)

// ============================================================================
// Export Form Metadata from YAML
// ============================================================================

export const FORM_CONFIG = formConfig
export const FORM_NAME = formConfig.formName
export const FORM_TITLE = formConfig.formTitle
export const FORM_DESCRIPTION = formConfig.formDescription

// ============================================================================
// Constants
// ============================================================================

const MIN_NUMBER = 1000000000
const MAX_NUMBER = 9999999999

// ============================================================================
// Enum Types (matching API schema)
// ============================================================================

export const DeviceTypeStatusEnum = z.enum(['research', 'testing', 'certified'])
export type DeviceTypeStatus = z.infer<typeof DeviceTypeStatusEnum>

export const DeviceTypeCategoryEnum = z.enum([
  'Weather Sensor',
  'Soil Sensor',
  'Camera',
  'Motion Sensor',
  'Relay Controller',
  'Solenoid Valve Controller',
  'Flow Meter',
  'Electric Current Sensor',
])
export type DeviceTypeCategory = z.infer<typeof DeviceTypeCategoryEnum>

// ============================================================================
// Form Schema (What user fills in)
// ============================================================================

export const AddDeviceTypeFormSchema = z.object({
  deviceTypeNumber: z.number().optional(), // Business key for edit mode
  deviceTypeName: z
    .string()
    .min(3, 'Name must be at least 3 characters')
    .max(60, 'Name cannot exceed 60 characters'),
  deviceTypeManufacturerNumber: z
    .coerce.number()
    .gte(MIN_NUMBER, 'Invalid manufacturer')
    .lte(MAX_NUMBER, 'Invalid manufacturer'),
  deviceTypeModel: z
    .string()
    .min(1, 'Model is required')
    .max(40, 'Model cannot exceed 40 characters'),
  deviceTypeCategory: DeviceTypeCategoryEnum.optional(),
  deviceTypeStatus: DeviceTypeStatusEnum.optional(),
})

export type AddDeviceTypeFormData = z.infer<typeof AddDeviceTypeFormSchema>

/**
 * Transform form data to API request format.
 * deviceTypeNumber is auto-generated by the backend.
 */
export function transformToApiRequest(formData: AddDeviceTypeFormData) {
  return {
    deviceTypeNumber: formData.deviceTypeNumber, // Business key for upsert
    deviceTypeName: formData.deviceTypeName,
    deviceTypeManufacturerNumber: formData.deviceTypeManufacturerNumber,
    deviceTypeModel: formData.deviceTypeModel,
    deviceTypeCategory: formData.deviceTypeCategory,
    deviceTypeStatus: formData.deviceTypeStatus,
  }
}
